import { useEffect, useMemo, useRef } from 'react';
import type { ScheduleConfig, ScheduleViewType } from '../../data/scheduleConfig';
import type { ZoomPillValue } from '../../components/schedule/ZoomPill';
import type { LensPillValue } from '../../components/schedule/LensPill';
import { useResolveScheduleView } from '../../hooks/useResolveScheduleDefaultView';
import { CONFIG_TO_ADMIN } from './viewStateHelpers';

interface UseScheduleViewChromeInput {
  userId?: string;
  isLensActive: boolean;
  scheduleConfig: Pick<ScheduleConfig, 'defaultView' | 'views'>;
  routeLensEnabled: boolean;
  zoomParam: ZoomPillValue | 'today';
  lensParam: LensPillValue;
  setZoom: (zoom: ZoomPillValue) => void;
  setLens: (lens: LensPillValue) => void;
}

interface UseScheduleViewChromeResult {
  zoom: ZoomPillValue;
  lens: LensPillValue;
  viewMode: 'day' | 'week' | 'month' | 'team' | 'year';
  zoomOptions: { value: ZoomPillValue; label: string }[];
  lensOptions: { value: LensPillValue; label: string }[];
}

export function useScheduleViewChrome({
  userId,
  isLensActive,
  scheduleConfig,
  routeLensEnabled,
  zoomParam,
  lensParam,
  setZoom,
  setLens,
}: UseScheduleViewChromeInput): UseScheduleViewChromeResult {
  const resolveScheduleView = useResolveScheduleView();
  const resolverAppliedRef = useRef(false);

  const lensDefault = useMemo<ZoomPillValue>(() => {
    if (!isLensActive) return 'week';
    return CONFIG_TO_ADMIN[scheduleConfig.defaultView] ?? 'week';
  }, [isLensActive, scheduleConfig.defaultView]);

  const zoom: ZoomPillValue = zoomParam === 'today' ? 'day' : zoomParam;
  const lens: LensPillValue = lensParam === 'route' && !routeLensEnabled ? 'calendar' : lensParam;

  useEffect(() => {
    if (!userId || resolverAppliedRef.current) return;
    const url = new URL(window.location.href);
    const hasZoom = url.searchParams.has('zoom');
    const hasLens = url.searchParams.has('lens');
    if (hasZoom && hasLens) {
      resolverAppliedRef.current = true;
      return;
    }

    void (async () => {
      const resolved = await resolveScheduleView({ userId, surface: 'desktop' });
      resolverAppliedRef.current = true;
      if (resolved.success) {
        setZoom(resolved.data.zoom === 'today' ? 'day' : resolved.data.zoom);
        setLens(resolved.data.lens);
        return;
      }
      if (!hasZoom) setZoom(lensDefault);
      if (!hasLens) {
        const defaultLens = isLensActive && scheduleConfig.defaultView === 'team' ? 'crew' : 'calendar';
        setLens(defaultLens);
      }
    })();
  }, [userId, isLensActive, scheduleConfig.defaultView, resolveScheduleView, setLens, setZoom, lensDefault]);

  useEffect(() => {
    if (lens === 'route' && zoom !== 'day') setZoom('day');
    if (lens === 'crew' && (zoom === 'month' || zoom === 'year')) setZoom('day');
  }, [lens, zoom, setZoom]);

  const zoomOptions = useMemo<{ value: ZoomPillValue; label: string }[]>(() => {
    const configured = isLensActive
      ? scheduleConfig.views
        .map((v: ScheduleViewType) => CONFIG_TO_ADMIN[v])
        .filter((v): v is ZoomPillValue => Boolean(v))
      : (['day', 'week', 'month', 'year'] as ZoomPillValue[]);
    const all = new Set<ZoomPillValue>([...configured, 'year']);
    if (lens === 'route') return [{ value: 'day', label: 'Day' }];
    if (lens === 'crew') return [{ value: 'day', label: 'Day' }, { value: 'week', label: 'Week' }];
    return (['day', 'week', 'month', 'year'] as ZoomPillValue[])
      .filter((z) => all.has(z))
      .map((z) => ({ value: z, label: z[0].toUpperCase() + z.slice(1) }));
  }, [isLensActive, scheduleConfig.views, lens]);

  const lensOptions = useMemo<{ value: LensPillValue; label: string }[]>(() => {
    const out: { value: LensPillValue; label: string }[] = [
      { value: 'calendar', label: 'Calendar' },
      { value: 'crew', label: 'Crew' },
    ];
    if (routeLensEnabled) out.push({ value: 'route', label: 'Route' });
    return out;
  }, [routeLensEnabled]);

  const viewMode: 'day' | 'week' | 'month' | 'team' | 'year' = lens === 'crew' ? 'team' : zoom;
  return { zoom, lens, viewMode, zoomOptions, lensOptions };
}
